import UserRow from "@components/dashboard/backoffice/userRow";
import Modal from "@components/Modal";
import NewUser from "./newUser";
import { useAuthActions, useAuthContext } from "@contexts/auth";
import { useBimActions } from "@contexts/bim";
import { useCostsActions } from "@contexts/costs";
import { useProjectActions } from "@contexts/projects";
import {
  dashboardPaths,
  resolveDashboardPathPermission,
} from "@outlets/configs/options";
import { permissionRoles } from "@utils/permissions";
import { useEffect, useMemo, useState } from "react";

// E-mails desses dois domínios são equipe interna (Ampli/Cartesian) — ficam isolados num
// grupo próprio em vez de aparecerem soltos ou misturados com usuários de construtora.
const INTERNAL_EMAIL_DOMAINS = ["@amplitecnologia.com", "@cartesianengenharia.com"];
const isInternalUser = (email) => INTERNAL_EMAIL_DOMAINS.some((domain) => (email || "").toLowerCase().endsWith(domain));
const sortByName = (list) => [...list].sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR"));

const Users = () => {
  const [users, setUsers] = useState([])
  const [companies, setCompanies] = useState([])
  const [buildings, setBuildings] = useState([])
  const [bimProjects, setBimProjects] = useState([])
  const [costsBuildings, setCostsBuildings] = useState([])
  const [selectedUser, setSelectedUser] = useState({})
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [resetConfirming, setResetConfirming] = useState(false)
  const [resetFeedback, setResetFeedback] = useState('')
  const [deleteConfirming, setDeleteConfirming] = useState(false)
  // { ok, message } — sem o `ok`, erro e sucesso saíam com o mesmo estilo verde.
  const [editFeedback, setEditFeedback] = useState(null)
  const [newUserModalVisible, setNewUserModalVisible] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState([])
  const [visibles, setVisibles] = useState({
    companies: false,
    roles: true,
    buildings: false,
    costs: false,
    bim: false,
    pages: false
  })
  const { listUsers, updateUser, deleteUser, resetPassword } = useAuthActions();
  const { user } = useAuthContext();
  const { getProjects } = useBimActions();
  const { getBuildings } = useCostsActions();
  const { listCompanies } = useProjectActions();

  const getBim = async () => {
    const res = await getProjects('all');
    setBimProjects(res?.res || [])
  }

  const getCosts = async () => {
    const res = await getBuildings('all')
    setCostsBuildings(res?.res || [])
  }

  const getCompanies = async () => {
    const res = await listCompanies();
    // Guarda defensiva: se a chamada falhar ou devolver um corpo inesperado (sem
    // `companies`/`buildings`), não pode deixar o estado como `undefined` — o `.map()` das
    // checkboxes de Construtoras/Obras (dentro do modal) quebrava a tela toda nesse caso.
    const sortByLabel = (list, field) => [...(list || [])].sort((a, b) => (a[field] || '').localeCompare(b[field] || '', 'pt-BR'))
    setCompanies(sortByLabel(res?.companies, 'name'))
    setBuildings(sortByLabel(res?.buildings, 'building_name'))
  }

  const getUsers = async () => {
    const res = await listUsers();
    setUsers(res?.users || [])
  }

  useEffect(() => {
    getUsers();
    getCompanies();
    getBim();
    getCosts()
  }, [])

  // Mesma apresentação de "Construtoras e Obras": grupos expansíveis, em ordem alfabética,
  // em vez de uma lista plana. Equipe interna (Ampli/Cartesian) fica isolada num grupo
  // próprio; os demais usuários agrupados por construtora — um usuário com mais de uma
  // construtora aparece em cada grupo a que pertence.
  const userGroups = useMemo(() => {
    const internal = []
    const noCompany = []
    const byCompany = {}
    for (const u of users) {
      if (isInternalUser(u.email)) { internal.push(u); continue }
      const companyIds = (u.companyId || []).map((c) => c?._id ?? c)
      if (companyIds.length === 0) { noCompany.push(u); continue }
      for (const companyId of companyIds) {
        const company = companies.find((c) => String(c._id) === String(companyId))
        const key = company ? String(company._id) : String(companyId)
        byCompany[key] = byCompany[key] || { key, name: company?.name || "Construtora desconhecida", users: [] }
        byCompany[key].users.push(u)
      }
    }
    const companyGroups = Object.values(byCompany)
      .map((group) => ({ ...group, users: sortByName(group.users) }))
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))

    const groups = [{ key: "internal", name: "Usuários Internos", users: sortByName(internal) }, ...companyGroups]
    if (noCompany.length > 0) groups.push({ key: "no-company", name: "Sem construtora", users: sortByName(noCompany) })
    return groups
  }, [users, companies])

  const toggleGroup = (key) => {
    setExpandedGroups(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]))
  }

  const handleEdit = (i) => {
    setSelectedUser({
      ...i,
      bimProjectsIds: i.bimProjectsIds || [],
      costsBuildingsIds: i.costsBuildingsIds || []
    });
    setResetConfirming(false)
    setResetFeedback('')
    setDeleteConfirming(false)
    setEditFeedback(null)
    setEditModalVisible(!editModalVisible)
  }

  const handleUserField = (e) => {
    const { name, value } = e.target;
    setSelectedUser(prev => ({ ...prev, [name]: value }))
  }

  const handleType = (e) => {
    let roles = [];
    if (selectedUser.roles) {
      roles = selectedUser.roles;
    }
    const { value } = e.target;
    if (!roles.includes(value)) {
      setSelectedUser(prev => ({
        ...prev,
        roles: [...roles, value]
      }));
    } else {
      const newRoles = roles.filter(i => i !== value);
      setSelectedUser(prev => ({
        ...prev,
        roles: newRoles
      }));
    }
  }

  const handleCompany = (e) => {
    const { value } = e.target;
    const userCompanies = selectedUser.companyId;
    if (!userCompanies.map(i => i._id).includes(value)) {
      setSelectedUser(prev => ({
        ...prev,
        companyId: [...userCompanies, { _id: value }]
      }));
    } else {
      const updatedCompanies = userCompanies.filter(i => i._id !== value);
      const updatedBuildings = selectedUser.buildingIds.filter(buildingId => {
        const building = buildings.find(b => b._id === buildingId);
        return building && building.company_id !== value;
      });
      setSelectedUser(prev => ({
        ...prev,
        companyId: updatedCompanies,
        buildingIds: updatedBuildings
      }));
    }
  }

  const handleSend = async () => {
    const res = await updateUser(selectedUser)
    if (res?.ok) {
      setEditFeedback({ ok: true, message: 'Salvo com sucesso.' })
      await getUsers()
    } else {
      setEditFeedback({ ok: false, message: res?.message || 'Não foi possível salvar.' })
    }
  }

  const confirmReset = async () => {
    const res = await resetPassword({ email: selectedUser.email })
    // Resposta de erro (500) vem como string crua, não objeto — sem essa checagem, um reset
    // que falhou mostrava a mesma mensagem de sucesso.
    setResetFeedback(
      typeof res === 'object' && res?.message
        ? res.message
        : 'Não foi possível resetar a senha.'
    )
    setResetConfirming(false)
  }

  const toggleActive = async () => {
    // `active === false` é o único jeito de estar inativo — ausência do campo (legado) ou
    // `true` contam como ativo.
    const active = selectedUser.active === false
    const res = await updateUser({ ...selectedUser, active })
    if (res?.ok) {
      setSelectedUser(prev => ({ ...prev, active }))
      setEditFeedback({ ok: true, message: active ? 'Usuário ativado.' : 'Usuário desativado.' })
      await getUsers()
    } else {
      setEditFeedback({ ok: false, message: res?.message || 'Não foi possível alterar o status.' })
    }
  }

  const confirmDelete = async () => {
    const res = await deleteUser(selectedUser._id)
    if (res?.ok) {
      setEditModalVisible(false)
      await getUsers()
    } else {
      setEditFeedback({ ok: false, message: res?.message || 'Não foi possível remover o usuário.' })
      setDeleteConfirming(false)
    }
  }

  const handleProjectsUpdate = (e) => {
    const { value, name } = e.target;
    const userProjects = selectedUser[name];
    if (!userProjects.includes(value)) {
      setSelectedUser(prev => ({
        ...prev,
        [name]: [...userProjects, value]
      }));
    } else {
      const updatedProjects = userProjects.filter(i => i !== value);
      setSelectedUser(prev => ({
        ...prev,
        [name]: updatedProjects
      }));
    }
  }

  const handlePagesUpdate = (e) => {
    const { value } = e.target;
    const userPages = selectedUser?.permissions?.pageViews || [];
    if (!userPages.includes(value)) {
      setSelectedUser(prev => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          pageViews: [...userPages, value]
        }
      }));
    } else {
      const updatedPages = userPages.filter(i => i !== value);
      setSelectedUser(prev => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          pageViews: updatedPages
        }
      }));
    }
  }

  const handleToggle = (e) => {
    const name = e.target.getAttribute("name")
    setVisibles((prev) => ({
      ...prev,
      [name]: !prev[name]
    }))
  }

  const selectAll = (e) => {
    const name = e.target.getAttribute("name")
    if (name === 'ampli-buildings') {
      setSelectedUser(prev => ({ ...prev, buildingIds: buildings.filter(i => selectedUser.companyId.map(i => i._id).includes(i.company_id)).map(i => i._id) }))
    } else if (name === 'companies') {
      setSelectedUser(prev => ({ ...prev, companyId: companies.map(i => { return { _id: i._id } }) }))
    } else if (name === 'costs') {
      setSelectedUser(prev => ({ ...prev, costsBuildingsIds: costsBuildings.map(i => i._id) }))
    } else {
      setSelectedUser(prev => ({ ...prev, bimProjectsIds: bimProjects.map(i => i._id) }))
    }
  }
  const deselectAll = (e) => {
    const name = e.target.getAttribute("name")
    if (name === 'ampli-buildings') {
      setSelectedUser(prev => ({ ...prev, buildingIds: [] }))
    } else if (name === 'companies') {
      setSelectedUser(prev => ({ ...prev, companyId: [] }))
    } else if (name === 'costs') {
      setSelectedUser(prev => ({ ...prev, costsBuildingsIds: [] }))
    } else {
      setSelectedUser(prev => ({ ...prev, bimProjectsIds: [] }))
    }
  }

  return (
    <>
      {editModalVisible && <Modal close={() => setEditModalVisible(false)}>
        <div className="edit-user-modal">
          <div className="user-details">
            <label>Nome</label>
            <input type="text" name="name" value={selectedUser.name || ''} onChange={handleUserField} />
            <label>E-mail</label>
            <input type="text" name="email" value={selectedUser.email || ''} onChange={handleUserField} />
          </div>
          {user.roles.includes('admin') &&
            <div className="user-details">
              <div className="flex">
                <button
                  className={`accordion-toggle ${visibles.roles ? "active" : ""}`}
                  name='roles' onClick={handleToggle}
                />
                <span name='roles' onClick={handleToggle}>Permissões</span>
              </div>
              {visibles.roles && <div className="companies grid gap-16 columns-3-tablet align-center">
                {permissionRoles.map(i => {
                  return <div key={i.value}>
                    <input type="checkbox" value={i.value} checked={selectedUser?.roles?.includes(i.value)} onChange={handleType} />
                    <label>{i.name}</label>
                  </div>
                })}
              </div>}
            </div>
          }
          {(selectedUser.roles.includes('admin') || selectedUser.roles.includes('ampli')) && <>
            <div className="user-details">
              <div className="flex">
                <button
                  className={`accordion-toggle ${visibles.companies ? "active" : ""}`}
                  name='companies' onClick={handleToggle}
                />
                <span name='companies' onClick={handleToggle}>Construtoras</span>
              </div>
              {visibles.companies && <>
                <p className="margin-top-4 clickable" name="companies" onClick={selectAll}>Selecionar todas</p>
                <p className="margin-top-4 clickable" name="companies" onClick={deselectAll}>Deselecionar todos</p>
                <div className="companies grid gap-16 columns-4-tablet align-center margin-top-8">
                  {(companies || []).map(i => {
                    return <div key={`${i._id}`}>
                      <input onChange={handleCompany} checked={selectedUser?.companyId?.map(i => i._id).includes(i._id)} type="checkbox" value={`${i._id}`} />
                      <label>{i.name}</label>
                    </div>
                  })}
                </div>
              </>
              }
            </div>
            <div className="user-details">
              <div className="flex">
                <button
                  className={`accordion-toggle ${visibles.buildings ? "active" : ""}`}
                  name='buildings' onClick={handleToggle}
                />
                <span name='buildings' onClick={handleToggle}>Obras AMPLI</span>
              </div>
              {visibles.buildings && <>
                <p className="margin-top-4 clickable" name="ampli-buildings" onClick={selectAll}>Selecionar todas</p>
                <p className="margin-top-4 clickable" name="ampli-buildings" onClick={deselectAll}>Deselecionar todos</p>
                <div className="companies grid gap-16 columns-4-tablet align-center">
                  {(buildings || []).filter(i => (selectedUser.companyId || []).map(i => i._id).includes(i.company_id)).map(i => {
                    return <div key={`${i._id}`}>
                      <input onChange={handleProjectsUpdate} name="buildingIds" checked={selectedUser?.buildingIds?.includes(i._id)} type="checkbox" value={`${i._id}`} />
                      <label>{i.building_name}</label>
                    </div>
                  })}
                </div>
              </>}
            </div>
          </>
          }
          {(selectedUser.roles.includes('costs') || selectedUser.roles.includes('costsAdmin')) &&
            <div className="user-details">
              <div className="flex">
                <button
                  className={`accordion-toggle ${visibles.costs ? "active" : ""}`}
                  name='costs' onClick={handleToggle}
                />
                <span name='costs' onClick={handleToggle}>Projetos Custos</span>
              </div>
              {visibles.costs && <>
                <p className="margin-top-4 clickable" name="costs" onClick={selectAll}>Selecionar todos</p>
                <p className="margin-top-4 clickable" name="costs" onClick={deselectAll}>Deselecionar todos</p>
                <div className="companies grid gap-16 columns-4-tablet align-center">
                  {(costsBuildings || []).map(i => {
                    return <div key={i._id}>
                      <input type="checkbox" value={i._id} checked={selectedUser?.costsBuildingsIds?.includes(i._id)} name="costsBuildingsIds" onChange={handleProjectsUpdate} />
                      <label>{i.name}</label>
                    </div>
                  })}
                </div>
              </>}
            </div>}
          {(selectedUser.roles.includes('bim') || selectedUser.roles.includes('bimAdmin')) &&
            <div className="user-details">
              <div className="flex">
                <button
                  className={`accordion-toggle ${visibles.bim ? "active" : ""}`}
                  name='bim' onClick={handleToggle}
                />
                <span name='bim' onClick={handleToggle}>Projetos BIM</span>
              </div>
              {visibles.bim && <>
                <p className="margin-top-4 clickable" name="bim" onClick={selectAll}>Selecionar todos</p>
                <p className="margin-top-4 clickable" name="bim" onClick={deselectAll}>Deselecionar todos</p>
                <div className="companies grid gap-16 columns-4-tablet align-center">
                  {(bimProjects || []).map(i => {
                    return <div key={i._id}>
                      <input type="checkbox" value={i._id} checked={selectedUser?.bimProjectsIds?.includes(i._id)} name="bimProjectsIds" onChange={handleProjectsUpdate} />
                      <label>{i.name}</label>
                    </div>
                  })}
                </div>
              </>}
            </div>}
          <div className="user-details">
            <div className="flex">
              <button
                className={`accordion-toggle ${visibles.pages ? "active" : ""}`}
                name='pages' onClick={handleToggle}
              />
              <span name='pages' onClick={handleToggle}>Telas</span>
            </div>
            {visibles.pages && <>
              <div className="companies grid gap-16 columns-4-tablet align-center">
                {dashboardPaths.map(({ label, ...path }) => {
                  const permission = resolveDashboardPathPermission({ label, ...path });
                  return <div key={permission}>
                    <input type="checkbox" value={permission} checked={selectedUser?.permissions?.pageViews?.includes(permission)} name="pages" onChange={handlePagesUpdate} />
                    <label>{label}</label>
                  </div>
                })}
              </div>
            </>}
          </div>
        </div>
        {selectedUser.active === false && (
          <p className="inactive-badge">Esta conta está desativada — não consegue fazer login.</p>
        )}
        {resetConfirming && (
          <div className="reset-password-confirm">
            <p>Confirma o reset da senha de {selectedUser.name} ({selectedUser.email})?</p>
            <button type="button" className="button button-accent" onClick={confirmReset}>Confirmar reset</button>
            <button type="button" className="button" onClick={() => setResetConfirming(false)}>Cancelar</button>
          </div>
        )}
        {resetFeedback && <p className="reset-password-feedback">{resetFeedback}</p>}
        {deleteConfirming && (
          <div className="reset-password-confirm">
            <p>Remover definitivamente a conta de {selectedUser.name} ({selectedUser.email})? Essa ação não pode ser desfeita.</p>
            <button type="button" className="button button-red" onClick={confirmDelete}>Confirmar remoção</button>
            <button type="button" className="button" onClick={() => setDeleteConfirming(false)}>Cancelar</button>
          </div>
        )}
        {editFeedback && (
          <p className={editFeedback.ok ? "reset-password-feedback" : "reset-password-feedback error"}>
            {editFeedback.message}
          </p>
        )}
        <div className="edit-user-actions">
          <button type="button" className="button button-accent" onClick={handleSend}>Salvar</button>
          <button type="button" className="button" onClick={() => setResetConfirming(true)}>Resetar senha</button>
          <button type="button" className="button" onClick={toggleActive}>
            {selectedUser.active === false ? 'Ativar' : 'Desativar'}
          </button>
          <button type="button" className="button button-red" onClick={() => setDeleteConfirming(true)}>Remover</button>
        </div>
      </Modal>}
      {newUserModalVisible && <Modal close={() => setNewUserModalVisible(false)}>
        <NewUser onSuccess={(res) => {
          if (res?.ok) {
            setNewUserModalVisible(false)
            getUsers()
          }
        }} />
      </Modal>}

      <div className="directory-header">
        <h2>Usuários</h2>
        <div className="directory-actions">
          <button type="button" className="button button-accent" onClick={() => setNewUserModalVisible(true)}>
            Novo Usuário
          </button>
        </div>
      </div>

      <div className="directory-list">
        {userGroups.map(group => {
          const expanded = expandedGroups.includes(group.key)
          return (
            <div className="directory-company" key={group.key}>
              <div
                role="button"
                tabIndex={0}
                className={`directory-company-row ${expanded ? "expanded" : ""}`}
                onClick={() => toggleGroup(group.key)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleGroup(group.key) }}
              >
                <span className="directory-company-name">{group.name}</span>
                <span className="directory-company-count">{group.users.length} usuário(s)</span>
              </div>

              {expanded && (
                group.users.length === 0 ? (
                  <p className="directory-empty">Nenhum usuário neste grupo.</p>
                ) : (
                  <div className="directory-users">
                    {group.users.map(u => (
                      <UserRow user={u} companies={companies} key={u._id} edit={() => handleEdit(u)} />
                    ))}
                  </div>
                )
              )}
            </div>
          )
        })}
      </div>
    </>
  );
};

export default Users;
